import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getUser } from "@/apis/user/user-apis";
import type { User } from "@/domain/user";
import { queryKeys } from "./keys";

interface UseUserOptions {
  enabled?: boolean;
}

interface UseUsersByLegacyIdsOptions {
  enabled?: boolean;
  limit?: number;
}

export function useUser(userId: number | null, options?: UseUserOptions) {
  return useQuery<User>({
    queryKey: queryKeys.users.detail(userId ?? 0),
    // Query is disabled when userId is null, so queryFn won't run with fallback value
    queryFn: () => getUser(userId ?? 0),
    enabled: options?.enabled !== false && userId != null,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUsersByLegacyIds(
  ids: number[],
  options?: UseUsersByLegacyIdsOptions
) {
  const limit = options?.limit ?? 50;
  const enabled = options?.enabled !== false && ids.length > 0;
  const uniqueIds = useMemo(
    () => Array.from(new Set(ids)).slice(0, limit),
    [ids, limit]
  );

  const results = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: queryKeys.users.detail(id),
      queryFn: () => getUser(id),
      enabled,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const usersMap = useMemo(() => {
    const map = new Map<number, User>();
    results.forEach((result, index) => {
      if (result.data) {
        map.set(uniqueIds[index], result.data);
      }
    });
    return map;
  }, [results, uniqueIds]);

  const loading = results.some(
    (result) => result.isLoading || result.isFetching
  );

  return { usersMap, loading };
}
